import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Tile, TileContent, TileText, Typography, TileStateIndicator
} from '@one-thd/sui-atomic-components';
import { Store, Delivery, Online } from '@one-thd/sui-icons';
import { getFulfillmentSubheaderLabel } from '../../utils/fulfillment-helpers';

const FulfillmentIcon = ({ refinementKey }) => {
  if (refinementKey === 'mock_browseAll') {
    return <Online color="primary" />;
  }
  if (refinementKey === '1z16d1c') {
    return <Store color="primary" />;
  }
  if (refinementKey === 'bwo6o') {
    return <Delivery color="primary" />;
  }
  return null;
};

FulfillmentIcon.propTypes = {
  refinementKey: PropTypes.string
};

FulfillmentIcon.defaultProps = {
  refinementKey: ''
};

const EmtHiddenLink = ({
  label, url, storeName, deliveryZip, isConsumerApp
}) => {
  const getHiddenLinkText = () => {
    if (label === 'In Store' && storeName) {
      return `${label} at ${storeName}`;
    }
    if (label.includes('Delivery') && !isConsumerApp) {
      return `${label} to ${deliveryZip || '...'}`;
    }
    return label;
  };

  return (
    /* SEO-friendly hidden link - Required for SEO significance. Reach out to SEO team if wanting to change */
    <a
      href={url}
      className="sui-hidden"
      aria-hidden="true"
      tabIndex="-1"
    >
      {/* H3 element required for SEO significance. Reach out to SEO team if wanting to change */}
      <h3>
        {getHiddenLinkText()}
      </h3>
    </a>
  );
};

EmtHiddenLink.propTypes = {
  label: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  storeName: PropTypes.string,
  deliveryZip: PropTypes.string,
  isConsumerApp: PropTypes.bool
};

EmtHiddenLink.defaultProps = {
  storeName: '',
  deliveryZip: '',
  isConsumerApp: false
};

const FulfillmentRefinement = ({
  dimension,
  id,
  onChange,
  refinement,
  selected,
  parentID,
  storeName,
  isConsumerApp,
  deliveryZip
}) => {
  const handleOnChange = useCallback(() => {
    const subHeaderLabel = getFulfillmentSubheaderLabel(refinement.label, storeName, isConsumerApp, deliveryZip);
    if (onChange) {
      onChange({ refinement, dimension, subHeaderLabel });
    }
  }, [refinement, dimension, onChange, storeName, isConsumerApp, deliveryZip]);

  const tileStyle = refinement.refinementKey === '1z16d1c'
    ? { marginBottom: '8px' }
    : {};

  return (
    <Tile
      aria-label={refinement.label}
      id={`${id}-${parentID}`}
      key={`${id}-${parentID}`}
      onChange={handleOnChange}
      data-refinement={refinement.label}
      data-refinementvalue={refinement.label}
      value={refinement.label}
      selected={selected}
      style={tileStyle}
    >
      <TileContent grow alignItems="start">
        <div className="sui-contents">
          <FulfillmentIcon refinementKey={refinement.refinementKey} />
        </div>
        <div className="sui-mt-2px">
          <div className="sui-inline-block">
            <TileText component="div" data-testid={`emt-link-${refinement.label}`}>
              <EmtHiddenLink
                label={refinement.label}
                url={refinement.url}
                storeName={storeName}
                deliveryZip={deliveryZip}
                isConsumerApp={isConsumerApp}
              />
              {refinement.label}

              {/* display store name */}
              {refinement.label === 'In Store' && storeName && (
                <Typography
                  variant="body-base"
                  color="subtle"
                  weight={selected ? 'bold' : undefined}
                  component="span"
                >
                  &nbsp;at {storeName}
                </Typography>
              )}
              {refinement.label.includes('Delivery') && !isConsumerApp && (
                <div className="sui-inline-block">
                  <Typography
                    variant="body-base"
                    color="subtle"
                    weight={selected ? 'bold' : undefined}
                  >
                    &nbsp;to {deliveryZip || '...'}
                  </Typography>
                </div>
              )}
            </TileText>
          </div>
          <span className="sui-absolute sui-mr-2 sui-mt-2px sui-right-0">
            <TileStateIndicator />
          </span>
        </div>
      </TileContent>
    </Tile>
  );
};

FulfillmentRefinement.propTypes = {
  dimension: PropTypes.shape({
    label: PropTypes.string,
    refinements: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  id: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  refinement: PropTypes.shape({
    label: PropTypes.string,
    refinementKey: PropTypes.string,
    selected: PropTypes.bool,
    url: PropTypes.string
  }).isRequired,
  selected: PropTypes.bool,
  parentID: PropTypes.string,
  storeName: PropTypes.string,
  isConsumerApp: PropTypes.bool,
  deliveryZip: PropTypes.string
};

FulfillmentRefinement.defaultProps = {
  id: null,
  selected: false,
  parentID: '',
  storeName: '',
  isConsumerApp: false,
  deliveryZip: ''
};

export { FulfillmentRefinement };